/**
 * Englische Textebene fuer die Produkt-Kategorieseiten.
 * Reihenfolge der itemDescs/galleryCaptions MUSS der Reihenfolge in products.js entsprechen.
 * Detailtexte der Herstellerseiten (Jultec/JRS/Kathrein) sind hier NICHT enthalten — bleiben deutsch.
 */
export const EN_CATEGORIES = {
    kopfstationstechnik: {
        label: "Headend Technology",
        short: "Cable headends from Grundig, Astro, Polytron — plus the Müller DHx series made in-house.",
        intro: [
            "Cable headends are the heart of every communal antenna system. We distribute headends from leading manufacturers — and manufacture the Müller DHx series ourselves: modular, custom-assembled and programmed. Our own products — headends and plug-in cards — come with a 4-year warranty.",
            "We also supply headends from other manufacturers.",
        ],
        dhx: [
            "Cable headend with 8 cassette slots — modular design, individually assembled and programmed.",
            "Cable headend with 4 cassette slots — the flexible mid-range for smaller systems.",
            "Compact cable headend with 2 cassette slots — ideal for retrofits and individual buildings.",
        ],
        dhxSection: {
            title: "Our headends: the Müller DHx-Basis.",
            sub: "Three sizes, one modular system: every DHx-Basis is assembled, programmed and tested by us — with a 4-year warranty.",
        },
        cassetteSection: {
            title: "Our plug-in cards (cassettes) for the DHx-Basis.",
            sub: "All cassettes fit the DH8-, DH4- and DH2-Basis — the type designation reveals the expansion stage and output. Configure your assembly directly in the configurator.",
            cols: ["Item no.", "Cassette", "Description"],
        },
        items: [
            { name: "Müller headends", desc: "Cable headends made in-house — DH8-Basis, DH4-Basis and DH2-Basis, custom-assembled and programmed." },
            { name: "Müller transmodulators", desc: "Standalone transmodulators from the Müller series — click through to the complete overview." },
            { name: "ASTRO", desc: "Headend technology by Astro — remaining stock on request." },
            { name: "GRUNDIG SAT Systems", desc: "Headend technology by Grundig — remaining stock on request." },
        ],
    },

    einzelumsetzer: {
        label: "Individual Transmodulators",
        short: "From AV and FM modulators to twin and quad transmodulators with CI slot.",
        intro: [
            "Transmodulators for professional channel processing: HDMI encoders, DVB-S2, DVB-T/C and QAM transmodulators as well as AV and FM modulators.",
        ],
        itemDescs: [
            "Twin transmodulator, 2× DVB-S/S2 transponders to DVB-S transponder 910–2200 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-S/S2 transponders to DVB-S transponder 910–2200 MHz, 2× CI",
            "Twin transmodulator, 2× DVB-T/T2/C bouquets to DVB-C (QAM), 2 independent channels 47–862 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-T/T2/C bouquets to DVB-C (QAM), 2 independent channels 47–862 MHz, 2× CI",
            "Twin transmodulator, 2× DVB-T/T2/C bouquets to DVB-T (COFDM), 2 independent channels 47–862 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-T/T2/C bouquets to DVB-T (COFDM), 2 independent channels 47–862 MHz, 2× CI",
            "Twin transmodulator, 2× DVB-T/T2/C bouquets to DVB-S transponder 910–2200 MHz, 1× CI",
            "HDMI single encoder, HDMI signal to QAM HD, MPEG 4",
            "HDMI twin encoder, HDMI signal to QAM HD, MPEG 4",
            "HDMI quad encoder, HDMI signal to QAM HD, MPEG 4",
            "HDMI single encoder, HDMI signal to COFDM, MPEG 4",
            "HDMI twin encoder, HDMI signal to COFDM, MPEG 4",
            "HDMI quad encoder, HDMI signal to COFDM, MPEG 4",
            "HDMI single encoder, HDMI signal to DVB/S, MPEG 4",
            "HDMI twin encoder, HDMI signal to DVB/S, MPEG 4",
            "HDMI quad encoder, HDMI signal to DVB/S, MPEG 4",
            "Single transmodulator HDMI to QAM or DVB-T, MPEG 4 / MPEG 2 SD, 30–860 MHz",
            "Twin transmodulator, 2× DVB-S2 bouquets to QAM, 2 independent channels 47–862 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-S2 bouquets to QAM, 2 independent channels 47–862 MHz, 2× CI",
            "Quad transmodulator, 4× DVB-S2 bouquets to QAM, 2 adjacent-channel pairs 47–862 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-S2 bouquets to DVB-T (COFDM), 2 independent channels 47–862 MHz, 1× CI",
            "Twin transmodulator, 2× DVB-S2 bouquets to DVB-T (COFDM), 2 independent channels 47–862 MHz, 2× CI",
            "Quad transmodulator, 4× DVB-S2 bouquets to DVB-T (COFDM), 2 independent channels 47–862 MHz, 1× CI",
            "DVB-S twin transmodulator, stereo, 2 DVB-S signals to 2 TV channels 47–862 MHz, 1× CI",
            "DVB-T twin transmodulator, stereo, 2 DVB-T signals to 2 TV channels 47–862 MHz, 1× CI",
            "Single AV modulator, stereo, adjacent-channel capable, AV signal to TV channel 47–862 MHz",
            "Twin AV modulator, stereo, adjacent-channel capable, 2 AV signals to 2 TV channels 47–862 MHz, 2 freely programmable channels",
            "FM stereo modulator",
            "Single transmodulator, FM programme to TV channel 47–862 MHz, on-screen",
            "Twin transmodulator, 2 FM programmes to 2 TV channels 47–862 MHz, on-screen",
        ],
        datasheet: "Datasheet (PDF)",
        gallery: {
            title: "Our transmodulators in detail.",
            captions: [
                "HDMI4-2QAM — 4× HDMI to QAM, MPEG-4 transmodulator, made in-house.",
                "SKU2-Twin — twin transmodulator, 2× DVB-S/S2 to DVB-S, 2× CI — shown with CI modules fitted.",
            ],
        },
    },

    antennenheizung: {
        label: "Antenna Heating",
        short: "Heating systems for ice-free reception — for antennas from 85 to 240 cm.",
        intro: [
            "Heating systems for 85–100 cm antennas and heaters for 120–240 cm antennas — for reliable, ice-free reception in extreme weather.",
        ],
        gallery: {
            title: "How the heater fits on the dish.",
            captions: [
                "HSA control box with sensor — mounted on the back of the dish, permanently weatherproof.",
                "Heating element at the dish rim — keeps the drain channel clear so ice cannot build up.",
                "Dish without heating — in snow and ice the surface quickly freezes over.",
            ],
        },
        items: [
            { name: "Heating systems for 85–100 cm antennas", desc: "Retrofit heating systems for standard dishes from 85 to 100 cm diameter." },
            { name: "Heaters for 120–240 cm antennas", desc: "Powerful heaters for large antennas from 120 to 240 cm diameter." },
        ],
    },

    antennen: {
        label: "Antennas",
        short: "Parabolic antennas from 65 to 240 cm as well as Masterfocus antennas.",
        intro: [
            "Parabolic antennas in all standard sizes — from the compact dish to the large professional antenna for large-scale systems.",
        ],
        gallery: {
            title: "The large antennas in practice.",
            captions: [
                "1.8 m antenna with base mount on the flat roof — feed system and distribution cabinet right next to it.",
                "OP 240 with base mount — large-scale reception even at demanding sites.",
            ],
        },
        items: [
            { name: "65–100 cm antennas", desc: "Compact dishes for individual and smaller multi-subscriber systems." },
            { name: "125 cm antennas", desc: "Medium-sized antennas with high reliability." },
            { name: "150 cm antennas", desc: "For demanding reception situations." },
            { name: "180 cm antennas", desc: "Large dishes for professional transmodulators and weak signal paths." },
            { name: "240 cm antennas", desc: "Maximum surface area for large-scale systems and professional reception." },
            { name: "Masterfocus antennas", desc: "High-quality Masterfocus dishes for the most demanding requirements." },
        ],
    },

    "sat-zf-verteilsysteme": {
        label: "SAT-IF Distribution",
        short: "Multi-switches & distribution technology from Jultec and Kathrein.",
        intro: [
            "SAT-IF distribution technology for multi-subscriber systems: multi-switches, splitters and amplifiers from leading manufacturers — for apartment blocks, hotels and large sites.",
        ],
        items: [
            { name: "Jultec", desc: "SAT-IF distribution technology by Jultec." },
            { name: "Kathrein", desc: "Distribution technology by Kathrein." },
        ],
        links: [
            {
                label: "See all Jultec devices",
                desc: "Overview page with subpages: fibre optic products (optics), JRM multi-switches with examples and the JRS system.",
            },
            {
                label: "See all KATHREIN multi-switches",
                desc: "Kathrein Line: multi-switches, single-cable systems, cascade accessories and network amplifiers.",
            },
        ],
    },

    "fibre-glas": {
        label: "Fibre Optic Systems",
        short: "Optical signal distribution via fibre optic — virtually loss-free for up to 10,000 connections.",
        intro: [
            "The universal LNBs convert the received satellite signals into digital light. Transmission to the individual distributors and end components runs via fibre optic cable, which has virtually no attenuation (approx. 0.3 dB per 1 km). This makes these LNBs ideal for systems with long signal paths.",
        ],
        features: [
            { t: "Revolutionary technology", d: "Unique, revolutionary reception and distribution technology." },
            { t: "Virtually loss-free", d: "Loss-free transmission of SAT, DVB-T and DAB signals." },
            { t: "Scalable", d: "Easily expandable up to 10,000 subscriber connections." },
        ],
        groups: [
            {
                title: "JULTEC fibre optic products",
                text: "From the transmitter at the receiving point to the converter at the distribution point — JULTEC covers the complete fibre optic chain.",
                link: {
                    to: "/produkte/sat-zf-verteilsysteme#glasfaser",
                    label: "See all Jultec fibre optic devices in detail",
                    desc: "Device tables with item numbers, inputs and outputs for JAO, JOL, JOS and JOM.",
                },
                items: [
                    { name: "JAO fibre optic transmitter", desc: "The optical transmitter at the receiving point — feeds JOS and JOL converters via fibre optic." },
                    { name: "JOL fibre broadband converter", desc: "For feeding single-cable multi-switches and coaxial cascades." },
                    { name: "JOL fibre-to-Quatro converter", desc: "For feeding multi-switches and coaxial cascades." },
                    { name: "JOS fibre-to-single-cable converter", desc: "For FTTH applications — fibre optic straight to the single-cable end device." },
                    { name: "JOM fibre-to-legacy converter", desc: "For FTTH applications — fibre optic to classic receivers." },
                ],
            },
            {
                title: "KATHREIN SAT optik",
                text: "Optical SAT distribution for long cable runs — virtually loss-free and interference-free, with automatic gain control (AGC) and common SC/APC and FC/PC connectors.",
                items: [
                    { name: "KEL 300/W — wideband feed system", desc: "The optical receiving source of SAT optik at the antenna point." },
                    { name: "OSQ 4/T — optical transmitter CWDM", desc: "Transmits four satellite positions plus terrestrial signals over a single fibre." },
                    { name: "OEQ 4 / OEQ 4/T — optical receivers", desc: "Quad and Quattro receivers (CWDM) convert the optical signal back to SAT-IF." },
                    { name: "OVS / OVC — optical splitters", desc: "2, 4, 8, 16 and 32-way (OVS) plus compact 2-/4-way splitters (OVC)." },
                    { name: "OAS — optical tap-offs", desc: "Tap individual signal paths off the fibre optic run." },
                    { name: "OCS — optical patch cables", desc: "SC/APC patch cables in lengths from 1 to 150 m for clean fibre runs." },
                    { name: "ODS — attenuators & adapters", desc: "Optical attenuators and simplex adapters for precise level matching." },
                    { name: "OWDM S2 — wavelength multiplexer", desc: "Bundles several wavelengths onto a single fibre." },
                    { name: "OPP 24 & OTS 1 — rack accessories", desc: "19″ fibre patch panel and optical termination for tidy distribution rooms." },
                ],
                link: {
                    to: "https://www.kathrein-ds.com/produkt-kategorie/tv-empfang-verteilung/sat-zf-verteiltechnik/sat-optik/",
                    label: "See KATHREIN SAT optik on kathrein-ds.com",
                    desc: "The complete product world with technical data and downloads (external link).",
                },
            },
        ],
    },

    lnb: {
        label: "LNB receiving converters",
        short: "Professional and standard feed systems from Swedish Microwave, Inverto and Invacom.",
        intro: [
            "Receiving converters for every requirement — from professional feed systems to the standard LNB.",
        ],
        items: [
            { name: "Professional feed systems", desc: "Swedish Microwave — professional LNBs for the highest demands." },
            { name: "Standard feed systems", desc: "Inverto & Invacom — reliable standard LNBs." },
            { name: "Block converters", desc: "Swedish Microwave — block converters for professional conversion." },
        ],
    },

    "adapter-multifeed": {
        label: "Adapters & multifeed rails",
        short: "Multifeed rails and adapters for antennas — for receiving several satellites.",
        intro: [
            "Adapters and multifeed rails for antennas — the basis for multifeed reception of several satellite positions on one dish.",
        ],
        items: [
            { name: "Multifeed rails", desc: "Attachment rails to hold several LNBs on one antenna." },
            { name: "Adapters", desc: "Adapters for various antenna and LNB combinations." },
        ],
    },

    infomedia: {
        label: "InfoMedia players",
        short: "Multimedia info-channel system for hotels, clinics & residential complexes.",
        intro: [
            "Multimedia info-channel system — the InfoMedia player plays PowerPoint presentations as well as MPEG and DIVX files and feeds them into the network as an info channel.",
        ],
        features: [
            { t: "PowerPoint", d: "Playback of PowerPoint multimedia files such as notice boards, graphics and still images. Stored on the InfoMedia player's hard disk." },
            { t: "Film playback", d: "Playing MPEG and DIVX files." },
            { t: "Web TV", d: "Playback of web TV broadcasts available on the internet (format: Windows Media Player)." },
            { t: "Timer", d: "Timer software for switching between the presentation programme and the MPEG programme." },
            { t: "Remote control", d: "TCP/IP 10/100 Mbit network card for remote programming of the system. Programming of single or multiple systems in one centrally controlled step. Automatic updates via the central FTP server." },
        ],
    },
};

/** Englische Kurz-Tags (Chips) der Kategorien */
export const EN_TAGS = {
    "EIGENE FERTIGUNG": "IN-HOUSE",
    "UMSETZER & ENCODER": "TRANSMODULATORS",
    WETTERSCHUTZ: "WEATHER PROTECTION",
    EMPFANG: "RECEPTION",
    VERTEILTECHNIK: "DISTRIBUTION",
    "OPTIK & GLASFASER": "OPTICS & FIBRE",
    EMPFANGSTECHNIK: "RECEPTION TECH",
    "ZUBEHÖR": "ACCESSORIES",
    MEDIASYSTEME: "MEDIA SYSTEMS",
};

/** Englische Kassettenbeschreibungen (Schluessel = code aus cassettes.js) */
export const EN_CASSETTES = {    "DQ-S2-Q1C": "4× DVB-S/S2 transponders to QAM, 47–862 MHz, 2 adjacent-channel pairs, 1× CI",
    "DT-S2-Q": "2× DVB-S/S2 transponders to QAM, 47–862 MHz, 2 independent channels, 1× CI",
    "DT-S2-Q-2CI": "2× DVB-S/S2 transponders to QAM, 47–862 MHz, 2 independent channels, 2× CI",
    "DT-S2-T-2CI": "2× DVB-S/S2 transponders to COFDM, 47–862 MHz, 2 independent channels, 2× CI",
    "DT-T2CC-2CI": "2× DVB-T/T2/C to QAM, 47–862 MHz, 2 independent channels, 2× CI",
    "DS-HDMI4-QAM": "HDMI single encoder, HDMI signal to QAM HD, MPEG 4, output channel 47–862 MHz",
    "DT-HDMI4-QAM": "HDMI twin encoder, 2× HDMI signals to QAM HD, MPEG 4, output 47–862 MHz",
    "DQ-HDMI4-QAM": "HDMI quad encoder, 4× HDMI signals to QAM HD, MPEG 4, output 47–862 MHz",
    "DS-HDMI4-S": "HDMI single module to 1× DVB-S 950–2150 MHz",
    "DT-HDMI4-S": "HDMI twin module to 1× DVB-S 950–2150 MHz",
    "DQ-HDMI4-S": "HDMI quad module to 1× DVB-S 950–2150 MHz",
    "DT-S2-DVBS": "DVB-S/S2 twin module, 1× CI, 2× 8PSK / QPSK 950–2150 MHz to DVB-S 950–2150 MHz",
    "DT-S2-DVBS-2CI": "DVB-S/S2 twin module, 2× CI, 2× 8PSK / QPSK 950–2150 MHz to DVB-S 950–2150 MHz",
    FREI: "Leave slot empty (for later expansion)",
};
